import { ref, computed, watch, onMounted, onScopeDispose, unref } from "vue";
import { useI18n } from "vue-i18n";
import { getAiCheckResult, triggerAiCheck } from "@/entities/transaction";
import { NOTIFICATIONS_CHANNEL_PREFIX } from "@/entities/notification";
import { useCentrifuge } from "@/shared/lib/centrifuge/useCentrifuge";
import { apiToUiData } from "./apiToUiData";

const DEFAULT_WS_TIMEOUT_MS = 180_000; // 3 минуты — обычно AI отвечает за ~1 мин

/**
 * State machine AI-проверки документов транзакции.
 *
 * Состояния:
 *  - "unchecked"  — проверка ни разу не запускалась (ai_result === null).
 *  - "triggering" — POST /documents/{tx_id}/ai-check в полёте.
 *  - "running"    — POST успешен, ждём WS-уведомление AI_CHECK_DOCS_COMPLETED.
 *  - "done"       — ai_result свежий, не устарел.
 *  - "stale"      — есть ai_result, но загружены новые документы после changed_at.
 *  - "error"      — POST провален, AI вернул ok:false, или WS timeout без свежих данных.
 */
export function useAiCheckScan(options = {}) {
  const { t } = useI18n();

  const state = ref(options.initialState || "unchecked");
  const payload = ref(null);
  const errorMessage = ref("");
  // True пока идёт первичная загрузка GET /documents в onMounted.
  // Используется чтобы баннер показывал skeleton, а не мерцал unchecked → done.
  const isInitialLoading = ref(true);

  // feedEvents оставлен для обратной совместимости со старыми пропами модалки
  const feedEvents = ref([]);

  // _progress — асимптотически растёт к 90 во время triggering/running
  const _progress = ref(0);
  let progressTimer = null;
  let wsTimeoutTimer = null;
  // Снимок lastCheckedAtRaw перед запуском — нужен в fallback после WS-timeout,
  // чтобы понять обновился ли changed_at.
  let runStartedChangedAt = null;

  function getTxId() {
    const raw = options.txId;
    return typeof raw === "function" ? raw() : unref(raw);
  }
  function getLocale() {
    const raw = options.locale;
    if (raw == null) return undefined;
    return typeof raw === "function" ? raw() : unref(raw);
  }
  function getUserUuid() {
    const raw = options.userUuid;
    if (raw == null) return null;
    return typeof raw === "function" ? raw() : unref(raw);
  }
  function getDocumentList() {
    const raw = options.documentList;
    if (raw == null) return [];
    const v = typeof raw === "function" ? raw() : unref(raw);
    return Array.isArray(v) ? v : [];
  }

  const uiData = computed(() => apiToUiData(payload.value, getLocale()));

  const progress = computed(() => {
    if (state.value === "done" || state.value === "stale") return 100;
    if (state.value === "unchecked" || state.value === "error") return 0;
    return Math.min(90, Math.round(_progress.value));
  });

  const lastCheckedAt = computed(() => uiData.value.lastCheckedAt);
  const lastCheckedAtRaw = computed(() => uiData.value.lastCheckedAtRaw);
  const hasAiResult = computed(() => uiData.value.hasAiResult);
  // Флаг от бэка: после последнего AI-запуска были изменения в пакете документов.
  const needsRecheck = computed(() => uiData.value.needsRecheck === true);
  const hasIssues = computed(
    () =>
      (uiData.value.score.criticalCount || 0) +
        (uiData.value.score.warningCount || 0) >
      0
  );

  // Топ-замечания для отображения в правой колонке Variant C (Ready state).
  const topCriticalSignals = computed(() => {
    const signals = uiData.value.signals || [];
    const bad = signals.filter((s) => s.severity === "bad");
    const warn = signals.filter((s) => s.severity === "warn");
    return [...bad, ...warn].slice(0, 2);
  });

  // UUID документов, реально проанализированных AI на последнем запуске.
  // Берём из ai_result.check_documents.results — это исторический снапшот,
  // он не меняется при последующих GET-запросах (в отличие от payload.items).
  const snapshotUuids = computed(() => {
    const results = payload.value?.ai_result?.check_documents?.results;
    if (!Array.isArray(results) || results.length === 0) return null;
    return new Set(results.map((r) => r?.document_uuid).filter(Boolean));
  });

  // Документы, добавленные после последней AI-проверки
  // (есть в текущем списке, но не было в снапшоте).
  // Дополнительная проверка по created_at — если снапшота нет, но lastCheckedAt есть.
  const addedDocsCount = computed(() => {
    const snap = snapshotUuids.value;
    if (snap) {
      return getDocumentList().filter((d) => d?.uuid && !snap.has(d.uuid))
        .length;
    }
    // Fallback по дате (если снапшот недоступен)
    const lastRaw = lastCheckedAtRaw.value;
    if (!lastRaw) return 0;
    const lastMs = new Date(lastRaw).getTime();
    if (Number.isNaN(lastMs)) return 0;
    return getDocumentList().filter((doc) => {
      const created = doc?.created_at;
      if (!created) return false;
      const ms = new Date(created).getTime();
      return !Number.isNaN(ms) && ms > lastMs;
    }).length;
  });

  // Документы из снапшота последней AI-проверки, которых больше нет в текущем списке.
  const removedDocsCount = computed(() => {
    const snap = snapshotUuids.value;
    if (!snap || snap.size === 0) return 0;
    const currentUuids = new Set(
      getDocumentList()
        .map((d) => d?.uuid)
        .filter(Boolean)
    );
    let count = 0;
    snap.forEach((uuid) => {
      if (!currentUuids.has(uuid)) count += 1;
    });
    return count;
  });

  // Основной источник истины — флаг needs_recheck от бэка.
  // Локальная UUID-эвристика остаётся как fallback (бэк может не успеть
  // обновить флаг между загрузкой документа и следующим GET).
  const hasDocChangesSinceCheck = computed(
    () =>
      needsRecheck.value ||
      addedDocsCount.value > 0 ||
      removedDocsCount.value > 0
  );

  // Алиас для обратной совместимости
  const hasNewDocsSinceCheck = hasDocChangesSinceCheck;

  function stopProgressTimer() {
    if (progressTimer != null) {
      clearInterval(progressTimer);
      progressTimer = null;
    }
  }
  function startProgressTimer() {
    stopProgressTimer();
    _progress.value = 0;
    progressTimer = setInterval(() => {
      _progress.value += (90 - _progress.value) * 0.08;
    }, 200);
  }
  function clearWsTimeout() {
    if (wsTimeoutTimer != null) {
      clearTimeout(wsTimeoutTimer);
      wsTimeoutTimer = null;
    }
  }

  /* ---------- localStorage-персистенция активного запуска ----------
   * Нужна чтобы после рефреша страницы:
   *   - восстановить состояние "running" с правильным остатком таймера;
   *   - показать error "Превышено время ожидания", если лимит уже истёк.
   */
  const STORAGE_KEY = (id) => `ai-check-running:${id}`;
  function persistRunStart(prevChangedAt) {
    const id = getTxId();
    if (!id) return;
    try {
      localStorage.setItem(
        STORAGE_KEY(id),
        JSON.stringify({
          startedAt: Date.now(),
          prevChangedAt: prevChangedAt ?? null,
        })
      );
    } catch (_) {
      // localStorage может быть недоступен (Safari private mode и т.п.) — игнорируем
    }
  }
  function clearPersistedRun() {
    const id = getTxId();
    if (!id) return;
    try {
      localStorage.removeItem(STORAGE_KEY(id));
    } catch (_) {
      /* noop */
    }
  }
  function readPersistedRun() {
    const id = getTxId();
    if (!id) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY(id));
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  async function fetchResult({ silent = false } = {}) {
    if (!getTxId()) return false;
    try {
      const res = await getAiCheckResult(getTxId());
      if (!res || res.success !== true || !res.payload) {
        if (!silent) {
          errorMessage.value = t("Не удалось получить результат AI-проверки.");
          state.value = "error";
          stopProgressTimer();
        }
        return false;
      }
      payload.value = res.payload;
      // hasAiResult — пересчёт от свежего payload
      return true;
    } catch (err) {
      console.error("[useAiCheckScan] fetchResult failed:", err);
      if (!silent) {
        errorMessage.value = t("Не удалось получить результат AI-проверки.");
        state.value = "error";
        stopProgressTimer();
      }
      return false;
    }
  }

  async function loadInitial() {
    if (!getTxId()) {
      isInitialLoading.value = false;
      return;
    }
    try {
      const ok = await fetchResult({ silent: true });
      const persisted = readPersistedRun();
      const timeoutMs = options.wsTimeoutMs ?? DEFAULT_WS_TIMEOUT_MS;

      // Есть запись о запуске в localStorage — проверяем, обновился ли changed_at.
      if (persisted) {
        const elapsed = Date.now() - (persisted.startedAt || 0);
        const changedAtAfter = lastCheckedAtRaw.value;
        const aiResponded =
          ok &&
          hasAiResult.value &&
          changedAtAfter &&
          changedAtAfter !== persisted.prevChangedAt;

        if (aiResponded) {
          // AI успел ответить пока мы были вне страницы → done
          clearPersistedRun();
          stopProgressTimer();
          _progress.value = 100;
          state.value = "done";
        } else if (elapsed >= timeoutMs) {
          // Лимит истёк, ответа нет → показываем error
          clearPersistedRun();
          stopProgressTimer();
          errorMessage.value = t(
            "Превышено время ожидания ответа от AI. Попробуйте обновить страницу или запустите проверку повторно."
          );
          state.value = "error";
        } else {
          // Ещё ждём — восстанавливаем running и перезапускаем таймер на остаток
          runStartedChangedAt = persisted.prevChangedAt;
          state.value = "running";
          startProgressTimer();
          clearWsTimeout();
          wsTimeoutTimer = setTimeout(
            handleWsTimeout,
            Math.max(1000, timeoutMs - elapsed)
          );
        }
        return;
      }

      // Нет активного запуска в localStorage — обычный путь
      if (ok && hasAiResult.value) {
        stopProgressTimer();
        _progress.value = 100;
        // watcher переведёт в stale, если есть свежие документы
        state.value = "done";
      } else {
        state.value = "unchecked";
      }
    } finally {
      isInitialLoading.value = false;
    }
  }

  async function start() {
    if (state.value === "triggering" || state.value === "running") return;
    if (!getTxId()) {
      state.value = "unchecked";
      return;
    }

    errorMessage.value = "";
    runStartedChangedAt = lastCheckedAtRaw.value;
    state.value = "triggering";
    startProgressTimer();

    let res;
    try {
      res = await triggerAiCheck(getTxId());
    } catch (err) {
      console.error("[useAiCheckScan] triggerAiCheck threw:", err);
      stopProgressTimer();
      clearPersistedRun();
      errorMessage.value = t(
        "AI-сервис временно недоступен. Попробуйте позже."
      );
      state.value = "error";
      return;
    }

    if (!res || res.success !== true || res.payload?.ok !== true) {
      stopProgressTimer();
      clearPersistedRun();
      const backendMsg =
        res?.error?.message ||
        (typeof res?.error === "string" ? res.error : "");
      errorMessage.value =
        backendMsg || t("AI-сервис временно недоступен. Попробуйте позже.");
      state.value = "error";
      return;
    }

    state.value = "running";
    persistRunStart(runStartedChangedAt);

    clearWsTimeout();
    const timeoutMs = options.wsTimeoutMs ?? DEFAULT_WS_TIMEOUT_MS;
    wsTimeoutTimer = setTimeout(handleWsTimeout, timeoutMs);
  }

  async function handleWsEvent(data) {
    if (state.value !== "running") return;
    const p = data?.payload || {};
    if (p.service !== "TEMPLATE-GENERATE") return;
    if (p.options?.status_code !== "AI_CHECK_DOCS_COMPLETED") return;
    const eventTxId = Number(p.options?.transaction_id);
    const currentTxId = Number(getTxId());
    if (!Number.isFinite(eventTxId) || eventTxId !== currentTxId) return;

    clearWsTimeout();
    clearPersistedRun();
    const ok = await fetchResult();
    if (ok) {
      stopProgressTimer();
      _progress.value = 100;
      state.value = "done";
    }
  }

  async function handleWsTimeout() {
    wsTimeoutTimer = null;
    if (state.value !== "running") return;

    // Fallback: возможно WS-сообщение потерялось, но результат уже в БД.
    const ok = await fetchResult({ silent: true });
    const updated =
      ok &&
      lastCheckedAtRaw.value &&
      lastCheckedAtRaw.value !== runStartedChangedAt;

    clearPersistedRun();

    if (updated) {
      stopProgressTimer();
      _progress.value = 100;
      state.value = "done";
      return;
    }

    stopProgressTimer();
    errorMessage.value = t(
      "Превышено время ожидания ответа от AI. Попробуйте обновить страницу или запустите проверку повторно."
    );
    state.value = "error";
  }

  function cancel() {
    clearWsTimeout();
    clearPersistedRun();
    stopProgressTimer();
    // Если уже есть результат — откатываемся в done/stale, иначе в unchecked
    if (hasAiResult.value) {
      _progress.value = 100;
      state.value = hasNewDocsSinceCheck.value ? "stale" : "done";
    } else {
      _progress.value = 0;
      state.value = "unchecked";
    }
  }

  function restart() {
    clearWsTimeout();
    stopProgressTimer();
    start();
  }

  // Stale-watcher: следит за изменениями списка документов (загрузка/удаление)
  // после последней AI-проверки.
  watch(
    [hasDocChangesSinceCheck, state],
    ([isStale, currentState]) => {
      if (isStale && currentState === "done") {
        state.value = "stale";
      } else if (!isStale && currentState === "stale") {
        state.value = "done";
      }
    },
    { flush: "post" }
  );

  // WS-подписка на NOTIFICATIONS канал пользователя.
  // useCentrifuge автоматически отписывается на unmount.
  const userUuid = getUserUuid();
  if (userUuid) {
    const channel = `${NOTIFICATIONS_CHANNEL_PREFIX}${userUuid}`;
    useCentrifuge(channel, handleWsEvent);
  }

  onMounted(() => {
    loadInitial();
  });

  onScopeDispose(() => {
    stopProgressTimer();
    clearWsTimeout();
  });

  return {
    state,
    progress,
    payload,
    uiData,
    feedEvents,
    lastCheckedAt,
    lastCheckedAtRaw,
    hasAiResult,
    needsRecheck,
    hasIssues,
    isInitialLoading,
    hasNewDocsSinceCheck,
    hasDocChangesSinceCheck,
    addedDocsCount,
    removedDocsCount,
    snapshotUuids,
    topCriticalSignals,
    errorMessage,
    start,
    cancel,
    restart,
  };
}
